'use client';

import { useState, useEffect } from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { formatPrice } from '@/lib/currency/format';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/Modal';
import { createClient } from '@/lib/supabase/client';
import PaymentCheckout from '@/components/payment/PaymentCheckout';
import GatewayRedirectForm from '@/components/payment/GatewayRedirectForm';
import type { RedirectForm } from '@/lib/payment/types';
import { Check, Ticket as TicketIcon, AlertCircle } from 'lucide-react';
import { serverDate } from '@/lib/time/serverClock';
import { useToast } from '@/lib/context/ToastContext';
import { useAuth } from '@/lib/context/AuthContext';
import RegistrationForm, { type RegistrationFormProps } from '@/components/registration/RegistrationForm';
import {
  fetchRegistrationNeeds,
  fetchSpecialtyCatalog,
  needsForm,
  postTicketChange,
  ticketChangeErrorKey,
  type RegistrationNeeds,
} from '@/lib/registration/client';
import type { SpecialtyOption } from '@/lib/specialties/types';
import type { QuestionAnswer } from '@/lib/tickets/questions';

type ServerErrors = NonNullable<RegistrationFormProps['serverErrors']>;

interface Ticket {
  id: string;
  name: string;
  price: number;
  currency: string | null;
  status: 'available' | 'sold_out' | 'discontinued';
  max_quantity: number | null;
  sold_quantity: number | null;
  sale_start_date: string | null;
  sale_end_date: string | null;
}

interface ChangeTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  enrollmentId: string;
  contentId: string;
  currentTicket: {
    id: string;
    name: string;
    price: number;
  } | null;
  paidAmount: number;
  onSuccess: () => void;
}

export default function ChangeTicketModal({
  isOpen,
  onClose,
  enrollmentId,
  contentId,
  currentTicket,
  paidAmount,
  onSuccess,
}: ChangeTicketModalProps) {
  const { t, language } = useLanguage();
  const toast = useToast();
  const { user, refreshProfile } = useAuth();
  const lang: 'uk' | 'en' = language === 'en' ? 'en' : 'uk';
  const supabase = createClient();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState('');
  const [upgradePayment, setUpgradePayment] = useState<{
    provider: string;
    widgetData: Record<string, unknown>;
    signature?: string;
    mode?: 'redirect' | 'widget';
    redirect?: RedirectForm;
  } | null>(null);
  // The new ticket's registration form: shown only when that ticket still needs something.
  const [formNeeds, setFormNeeds] = useState<RegistrationNeeds | null>(null);
  const [specialties, setSpecialties] = useState<SpecialtyOption[]>([]);
  // Set once per server refusal, so the form re-marks fields only when the server spoke again.
  const [serverErrors, setServerErrors] = useState<ServerErrors | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchTickets();
    }
  }, [isOpen, contentId]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const { isWithinWindow } = await import('@/lib/availability/resolveWindow');
      const { isWithinUpgradeWindow } = await import('@/lib/utils/upgrade-window');
      const [{ data, error }, { data: eventRow }] = await Promise.all([
        supabase
          .from('tickets')
          .select('id, name, price, currency, status, max_quantity, sold_quantity, sale_start_date, sale_end_date, sale_start_anchor, sale_end_anchor')
          .eq('content_id', contentId)
          .eq('status', 'available')
          .eq('requires_promocode', false)
          .order('price', { ascending: true }),
        supabase
          .from('content')
          .select('start_date, end_date')
          .eq('id', contentId)
          .single(),
      ]);

      if (error) throw error;

      const eventTimes = {
        start_date: eventRow?.start_date ?? null,
        end_date: eventRow?.end_date ?? null,
      };
      const now = serverDate();
      const eventEnded = !!eventRow?.end_date && new Date(eventRow.end_date) < now;
      const upgradeWindowOpen = isWithinUpgradeWindow(eventRow?.end_date ?? null, now);
      const validTickets = (data || []).filter(t => {
        if (t.status === null) return false;
        // Post-event: keep tickets whose price would be a strict upgrade, even if
        // their sale window has closed – but only inside the upgrade window.
        if (eventEnded && upgradeWindowOpen && t.price > paidAmount) {
          return true;
        }
        return isWithinWindow(
          {
            from: t.sale_start_date,
            from_anchor: t.sale_start_anchor ?? 'absolute',
            until: t.sale_end_date,
            until_anchor: t.sale_end_anchor ?? 'absolute',
          },
          eventTimes
        );
      }) as Ticket[];
      setTickets(validTickets);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTicketSelect = (ticketId: string) => {
    if (ticketId === currentTicket?.id) return;

    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket) return;

    // Check if sold out
    if (ticket.max_quantity !== null) {
      const available = ticket.max_quantity - (ticket.sold_quantity || 0);
      if (available <= 0) return;
    }

    // Downgrades are organizer-only (DOWNGRADE_NOT_SELF_SERVICE, the server
    // guard this client-side check mirrors) - never let a participant even
    // select a cheaper ticket, so the request that would be refused is
    // never sent.
    if (ticket.price < paidAmount) return;

    setSelectedTicketId(ticketId);
    setError('');
  };

  const loadNeeds = (ticketId: string) =>
    fetchRegistrationNeeds({ channel: 'site', contentId, ticketId, enrollmentId });

  /** The form computes its shown fields on mount, so it opens only once the catalog is in. */
  const showForm = async (needs: RegistrationNeeds, errors: ServerErrors | null): Promise<void> => {
    const catalog = await fetchSpecialtyCatalog();
    if (catalog === null) {
      setError(t('profile.tickets.changeError'));
      return;
    }
    setSpecialties(catalog);
    setFormNeeds(needs);
    setServerErrors(errors);
  };

  const profileSaved = async () => {
    try {
      if (user) await refreshProfile(user.id, true);
    } catch {
      // The form already saved; a stale cached profile is not worth an error.
    }
  };

  const closeForm = () => {
    setFormNeeds(null);
    setServerErrors(null);
  };

  const submitChange = async (ticketId: string, answers: QuestionAnswer[], formOpen: boolean) => {
    const result = await postTicketChange({ enrollmentId, newTicketId: ticketId, language, answers });
    switch (result.kind) {
      case 'changed':
        if (!result.answersStored) toast.error(t('profile.tickets.answersNotStoredOnChange'));
        onSuccess();
        resetAndClose();
        return;
      case 'payment':
        // Cancelling the payment returns to the ticket list, as before.
        closeForm();
        setUpgradePayment(result.checkout);
        return;
      case 'requirements': {
        const errors: ServerErrors = {
          ...(result.missingFields ? { missingFields: result.missingFields } : {}),
          ...(result.answerErrors ? { answerErrors: result.answerErrors } : {}),
        };
        if (formOpen) {
          setServerErrors(errors);
          return;
        }
        const fresh = await loadNeeds(ticketId);
        if (!fresh.ok) {
          setError(t('profile.tickets.changeError'));
          return;
        }
        await showForm(fresh.needs, errors);
        return;
      }
      case 'error': {
        // Translate the route's errorCode; never show its English text.
        const key = ticketChangeErrorKey(result.errorCode, (k) => t(k) !== k);
        setError(key ? t(key) : t('profile.tickets.changeError'));
        return;
      }
    }
  };

  const handleChangeTicket = async () => {
    if (!selectedTicketId) return;

    setChanging(true);
    setError('');

    try {
      const result = await loadNeeds(selectedTicketId);
      if (!result.ok) {
        setError(t('profile.tickets.changeError'));
      } else if (needsForm(result.needs)) {
        await showForm(result.needs, null);
      } else {
        await submitChange(selectedTicketId, [], false);
      }
    } catch {
      setError(t('profile.tickets.changeError'));
    } finally {
      setChanging(false);
    }
  };

  // Handed to the form, so it never throws.
  const submitForm = async (answers: QuestionAnswer[]) => {
    if (!selectedTicketId) return;
    setChanging(true);
    setError('');
    try {
      await submitChange(selectedTicketId, answers, true);
    } catch {
      setError(t('profile.tickets.changeError'));
    } finally {
      setChanging(false);
    }
  };

  const cancelForm = () => {
    if (changing) return;
    closeForm();
    setError('');
  };

  const resetAndClose = () => {
    setSelectedTicketId(null);
    setError('');
    setUpgradePayment(null);
    closeForm();
    onClose();
  };

  const handleClose = () => {
    if (!changing) resetAndClose();
  };

  const getSelectedTicket = () => tickets.find(t => t.id === selectedTicketId);

  const getPriceDifference = () => {
    const selectedTicket = getSelectedTicket();
    if (!selectedTicket) return 0;
    return selectedTicket.price - paidAmount;
  };

  const otherTickets = tickets.filter(t => t.id !== currentTicket?.id);
  // One currency per event: any ticket of the event carries it.
  const flowCurrency = tickets[0]?.currency;

  // Render payment widget when upgrade requires payment
  if (upgradePayment) {
    if (upgradePayment.mode === 'redirect' && upgradePayment.redirect) {
      return (
        <GatewayRedirectForm
          redirect={upgradePayment.redirect}
          message={t('courseDetails.paymentRedirecting')}
          continueLabel={t('courseDetails.paymentContinue')}
          cancelLabel={t('courseDetails.paymentCancel')}
          onCancel={() => setUpgradePayment(null)}
        />
      );
    }
    return (
      <PaymentCheckout
        provider={upgradePayment.provider}
        widgetData={upgradePayment.widgetData}
        signature={upgradePayment.signature}
        onSuccess={() => {
          setUpgradePayment(null);
          onSuccess();
          handleClose();
        }}
        onError={() => {
          setUpgradePayment(null);
          setError(t('profile.tickets.paymentFailed') || 'Payment failed');
        }}
        onClose={() => {
          setUpgradePayment(null);
        }}
      />
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('profile.tickets.changeTicketTitle')}
      size="lg"
      closeOnBackdropClick={!changing}
      closeOnEscape={!changing}
    >
      {formNeeds && selectedTicketId ? (
        <div className="space-y-4">
          <p className="font-medium text-gray-900">{t('profile.tickets.moreDetailsNeeded')}</p>
          <div>
            <RegistrationForm
              channel="site"
              contentId={contentId}
              ticketId={selectedTicketId}
              needs={formNeeds}
              specialties={specialties}
              serverErrors={serverErrors}
              submitLabel={getPriceDifference() > 0 ? t('profile.tickets.payAndChange') : t('profile.tickets.changeTicketButton')}
              busy={changing}
              onProfileSaved={profileSaved}
              onSubmit={submitForm}
              onCancel={cancelForm}
            />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}
        </div>
      ) : (
      <>
      <div className="space-y-6">
        {/* Current Ticket */}
        {currentTicket && (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-2">{t('profile.tickets.currentTicket')}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TicketIcon className="h-5 w-5 text-blue-500" />
                <span className="font-medium">{currentTicket.name}</span>
              </div>
              <span className="font-semibold">
                {paidAmount > 0 ? formatPrice(paidAmount, flowCurrency, lang) : t('profile.tickets.free')}
              </span>
            </div>
          </div>
        )}

        {/* Available Tickets */}
        <div>
          <p className="text-sm text-gray-500 mb-3">{t('profile.tickets.selectNewTicket')}</p>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : otherTickets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <TicketIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>{t('profile.tickets.noOtherTickets')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {otherTickets.map(ticket => {
                const isSelected = selectedTicketId === ticket.id;
                const available = ticket.max_quantity !== null
                  ? ticket.max_quantity - (ticket.sold_quantity || 0)
                  : null;
                const isSoldOut = available !== null && available <= 0;
                const priceDiff = ticket.price - paidAmount;
                // Downgrades are organizer-only (DOWNGRADE_NOT_SELF_SERVICE)
                // - a cheaper ticket stays visible for transparency, but is
                // disabled and never selectable, matching the server guard
                // this client-side rule mirrors.
                const isDowngrade = !isSoldOut && priceDiff < 0;
                const isDisabled = isSoldOut || isDowngrade;

                return (
                  <div
                    key={ticket.id}
                    onClick={() => !isDisabled && handleTicketSelect(ticket.id)}
                    className={`border-2 rounded-lg p-4 transition-all ${
                      isDisabled
                        ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                        : isSelected
                          ? 'border-blue-500 bg-blue-50 cursor-pointer'
                          : 'border-gray-200 hover:border-blue-300 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{ticket.name}</span>
                          {isSoldOut && (
                            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                              {t('profile.tickets.soldOut')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm">
                          <span className="text-gray-900 font-semibold">
                            {ticket.price === 0 ? t('profile.tickets.free') : formatPrice(ticket.price, ticket.currency, lang)}
                          </span>
                          {priceDiff > 0 ? (
                            <span className="text-orange-600">
                              +{formatPrice(priceDiff, ticket.currency, lang)} {t('profile.tickets.upgrade')}
                            </span>
                          ) : isDowngrade ? (
                            <span className="text-gray-500">
                              {t('crm.ticketChange.errors.DOWNGRADE_NOT_SELF_SERVICE')}
                            </span>
                          ) : (
                            <span className="text-green-600">
                              {t('profile.tickets.noAdditionalPayment')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-center w-8 h-8">
                        {isSelected ? (
                          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                            <Check className="w-5 h-5 text-white" />
                          </div>
                        ) : (
                          <div className={`w-8 h-8 rounded-full border-2 ${
                            isDisabled ? 'border-gray-300' : 'border-gray-300'
                          }`} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Price Difference Summary */}
        {selectedTicketId && (
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">{t('profile.tickets.priceDifference')}</span>
              <span className={`font-semibold ${getPriceDifference() > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {getPriceDifference() > 0 ? `+${formatPrice(getPriceDifference(), flowCurrency, lang)}` : t('profile.tickets.noAdditionalPayment')}
              </span>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <Modal.Footer>
        <Button
          variant="secondary"
          className="flex-1"
          onClick={handleClose}
          disabled={changing}
        >
          {t('profile.cancel')}
        </Button>
        <Button
          variant="primary"
          className="flex-1"
          onClick={handleChangeTicket}
          disabled={!selectedTicketId || changing}
        >
          {changing
            ? t('profile.tickets.changing')
            : getPriceDifference() > 0
              ? t('profile.tickets.payAndChange')
              : t('profile.tickets.changeTicketButton')
          }
        </Button>
      </Modal.Footer>
      </>
      )}
    </Modal>
  );
}
